/**
 * ReminderTimingPicker — opt-in dropdown for the daily 8 AM UTC digest.
 *
 * Same five values as the Payments selector (the backend uses one shared
 * `_VALID_REMINDER_TIMINGS` set across all item types). NONE is the
 * default, so unset items never surprise users with email.
 *
 * Two layouts:
 *   • default (stacked label + select)
 *   • `inline` — for tight edit-drawer rows where the label sits beside
 *     the control instead of above it.
 *
 * Used by Task / Commitment / Call / Reading create + edit forms.
 * The Payments form keeps its own copy (predates this component) so we
 * don't introduce a behavioural drift on the most-tested surface.
 */

const REMINDER_TIMINGS = [
  ['NONE',        'No reminder'],
  ['ON_DUE_DATE', 'On due date'],
  ['1_DAY',       '1 day before'],
  ['3_DAYS',      '3 days before'],
  ['7_DAYS',      '7 days before'],
];

export const ReminderTimingPicker = ({
  value,
  onChange,
  testId = 'reminder-timing-select',
  inline = false,
  disabled = false,
  helperText,
}) => {
  const safeValue = REMINDER_TIMINGS.some(([v]) => v === value) ? value : 'NONE';

  const select = (
    <select
      data-testid={testId}
      value={safeValue}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      aria-label="Reminder timing"
      className="px-3 py-2 rounded-[10px] text-sm outline-none disabled:opacity-50"
      style={{
        border: '1px solid var(--pd-border)',
        background: 'var(--pd-panel)',
        color: 'var(--pd-text)',
        minWidth: 140,
      }}
    >
      {REMINDER_TIMINGS.map(([v, label]) => (
        <option key={v} value={v}>{label}</option>
      ))}
    </select>
  );

  if (inline) {
    return (
      <div className="flex items-center gap-3">
        <label
          className="text-[11px] uppercase tracking-[0.08em] font-semibold flex-shrink-0"
          style={{ color: 'var(--pd-subtle)' }}
        >
          Reminder
        </label>
        {select}
        {helperText && (
          <span className="text-[10px]" style={{ color: 'var(--pd-subtle)' }}>
            {helperText}
          </span>
        )}
      </div>
    );
  }

  return (
    <div>
      <label
        className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5"
        style={{ color: 'var(--pd-subtle)' }}
      >
        Reminder
      </label>
      {select}
      {helperText && (
        <p className="text-[10px] mt-1" style={{ color: 'var(--pd-subtle)' }}>
          {helperText}
        </p>
      )}
    </div>
  );
};

export default ReminderTimingPicker;
